import React from 'react'
import { mergeClasses } from '@/helpers/styling'
import { ConfigurationMode, useConfigModeUrl, useConfigMode } from "@/components/editor/configuration-mode"
import { TopBarButton } from '@/components/editor/configuration-top-bar/primitives'
import { Bug, Eye, Pencil } from 'lucide-react'
import { SvgIcon, SvgIconType } from '@/components/svg-icon'

export function ConfigurationModeSwitch() {
  return (
    <SwitchButtons>
      <SwitchButton mode="view"
                    icon={Eye}
                    title="View the application as the user would see" >
        Preview
      </SwitchButton>
      <SwitchButton mode="debug"
                    icon={Bug}
                    title="View the application with debug tracking enabled">
        Debug
      </SwitchButton>
      <SwitchButton mode="edit"
                    icon={Pencil}
                    title="Editor mode">
        Edit
      </SwitchButton>
    </SwitchButtons>
  )
}

function SwitchButtons(props: {
  children: React.ReactNode
}) {
  return (
    <div className="flex gap-0.5 bg-[#3e3475] p-[2px] mr-1 rounded-md">
      {props.children}
    </div>
  )
}

function SwitchButton(props: {
  mode: ConfigurationMode
  title: string
  icon: SvgIconType
  children: React.ReactNode
}) {
  const mode = useConfigMode()
  const url = useConfigModeUrl(props.mode)
  const active = mode === props.mode

  return (
    <TopBarButton title={props.title}
                  to={url}
                  className={mergeClasses(`
                    h-[28px] !px-[12px]
                    hover:bg-[#4e4497] active:bg-[#4e4497]
                    hover:text-white
                    focus:!outline focus:!outline-1 focus:!outline-opacity-50
                  `, {
                    "bg-[#51479e] bg-opacity-100 text-gray-100": active,
                    "text-gray-300 opacity-75":   !active,
                    "focus:!outline-indigo-400":  props.mode === "view",
                    "focus:!outline-yellow-400":  props.mode === "debug",
                    "focus:!outline-red-400":     props.mode === "edit",
                  })}>
      <SvgIcon icon={props.icon}
               size={14}
               className={mergeClasses({
                 "hover:text-indigo-200":  props.mode === "view",
                 "hover:text-yellow-200":  props.mode === "debug",
                 "hover:text-red-200":     props.mode === "edit",
                 "text-indigo-400":        props.mode === "view" && active,
                 "text-yellow-400":        props.mode === "debug" && active,
                 "text-red-400":           props.mode === "edit" && active
               })} />
      {props.children}
    </TopBarButton>
  )
}
